import type { MemoryRow } from '@/api/memories.ts'
import type { I18n } from '@/i18n/index.ts'

/**
 * Sorting one flat list of preferences into the two things it actually is (AGM-090/093).
 *
 * The partition is the user's mental model, not the schema's: a preference either applies
 * everywhere or belongs to one named agent. Everything here is pure so the rules can be
 * tested, and there are three of them worth stating:
 *
 *  - a row with no agent is global. A row whose agent id arrives without a display name is
 *    still that agent's, and falls back to the id rather than silently joining the global
 *    pile — mislabeling an agent's preference as "Everywhere" would tell someone their
 *    answer-shortening request applies to a colleague-facing agent when it does not;
 *  - agents are listed alphabetically IN THE READER'S LANGUAGE, with "Everywhere" pinned
 *    first. Ordering by count would reshuffle the filter under the reader every time a
 *    preference is learned;
 *  - an agent that has been retired still gets its group. Its memories survive archival and
 *    stay the user's to read and forget (AGM-093), so the filter has to keep offering it.
 */

export type MemoryFilter = 'all' | 'global' | { agentId: string }

export type MemoryGroupOption = {
  /** `all` and `global` are singletons; everything else is one agent. */
  filter: MemoryFilter
  label: string
  count: number
}

/** null/undefined agent id = a preference that applies everywhere. */
export const isGlobal = (row: MemoryRow): boolean => !row.agentId

export const agentNameOf = (row: MemoryRow): string | null => {
  if (!row.agentId) return null
  const name = row.agentDisplayName?.trim()
  return name && name !== '' ? name : row.agentId
}

export const filterMemories = (rows: MemoryRow[], filter: MemoryFilter): MemoryRow[] => {
  if (filter === 'all') return rows
  if (filter === 'global') return rows.filter(isGlobal)
  return rows.filter((row) => row.agentId === filter.agentId)
}

/**
 * The options the filter offers, built from what is actually in the list.
 *
 * Never invents an agent with zero preferences, and never hides one that has some — the
 * filter is a description of the data, so a group that appears is a group that has content.
 */
export const memoryGroupOptions = (
  fmt: Pick<I18n, 'compare'>,
  rows: MemoryRow[],
  labels: { all: string; everywhere: string }
): MemoryGroupOption[] => {
  const byAgent = new Map<string, { label: string; count: number }>()
  let globalCount = 0

  for (const row of rows) {
    if (isGlobal(row)) {
      globalCount += 1
      continue
    }
    const agentId = row.agentId as string
    const existing = byAgent.get(agentId)
    // The first non-empty display name wins; a later row that arrives without one must not
    // downgrade the group's label back to a raw id.
    const label = agentNameOf(row) ?? agentId
    byAgent.set(agentId, {
      label: existing && existing.label !== agentId ? existing.label : label,
      count: (existing?.count ?? 0) + 1
    })
  }

  const options: MemoryGroupOption[] = [{ filter: 'all', label: labels.all, count: rows.length }]
  if (globalCount > 0) options.push({ filter: 'global', label: labels.everywhere, count: globalCount })

  const agents = [...byAgent.entries()]
    .map(([agentId, value]) => ({ filter: { agentId }, label: value.label, count: value.count }) as MemoryGroupOption)
    // The active locale's collator, not the browser's default: `localeCompare()` with no
    // argument sorted by whatever language the browser was set to rather than the one on
    // screen, and it put "Ática" after "Zeus" for a reader who had neither. It folds case
    // and accents and reads embedded numbers, so "Base 10" lands after "Base 9" (LOC-023).
    .sort((a, b) => fmt.compare(a.label, b.label))

  return [...options, ...agents]
}

/**
 * Keeps a filter that arrived from outside the list selectable.
 *
 * The chat surface links here filtered to one agent (AGM-092), and that agent may have no
 * preferences yet — the answer disclosed that memory was used, not that this agent learned
 * anything. Without an option to match, the `<select>` value would name a group that is not
 * in its list and the browser would display the first one instead, quietly claiming the page
 * is showing everything while it shows nothing.
 */
export const withFilterOption = (
  options: MemoryGroupOption[],
  filter: MemoryFilter,
  label: string
): MemoryGroupOption[] => {
  if (filter === 'all' || filter === 'global') return options
  const present = options.some(
    (option) => typeof option.filter === 'object' && option.filter.agentId === filter.agentId
  )
  return present ? options : [...options, { filter, label, count: 0 }]
}

/** Serialised for a `<select>` value, which can only hold a string. */
export const filterToValue = (filter: MemoryFilter): string =>
  filter === 'all' || filter === 'global' ? filter : `agent:${filter.agentId}`

export const valueToFilter = (value: string): MemoryFilter => {
  if (value === 'all' || value === 'global') return value
  return { agentId: value.startsWith('agent:') ? value.slice('agent:'.length) : value }
}
